<template>
  <div class="app-results">
    <div class="app-results__bar">
      <div class="app-results__bar__number">
        {{ ((resultsNumber | defaultView) + ' Results') | t }}
      </div>
    </div>

    <div class="days-pagination">
      <button class="days-pagination__control prev" @click="ChangeDate(-1)">
        <span class="material-icons-outlined">navigate_before</span>
      </button>
      <span class="days-pagination__control label">{{ DateDisplay }}</span>
      <button class="days-pagination__control next" @click="ChangeDate(1)">
        <span class="material-icons-outlined">navigate_next</span>
      </button>
    </div>

    <div class="app-results__data">
      <article v-if="!results.length & !showPreloader" class="result-item result-item--no-results">
        <span class="material-icons-outlined">error_outline</span>
        <p>
          Sorry, there was no activity found.
          <a
            :href="noResultUrl"
            title='"Alert when activities get posted" | t'
            >Click here</a
          >
          to get an alert when activities get posted.
        </p>
      </article>
      <article v-if="showPreloader" class="result-item result-item--no-results">
        <AppPreloader />
      </article>
      <template v-if="!showPreloader">
        <article v-for="item in results" :key="item.nid" class="result-item">
          <div class="result-item__head">
            <button class="result-item__head__title" @click="showModal(item)">
              {{ item.name }}
            </button>
          </div>
          <div class="result-item__body">
            <div class="result-item__body__col">
              <div class="label">{{ 'Time:' | t }}</div>
              <div class="data">
                <p>{{ item.time_start }} - {{ item.time_end }}</p>
              </div>
            </div>
            <div class="result-item__body__col">
              <div class="label">{{ 'Location:' | t }}</div>
              <div class="data">
                <p>{{ item.location | defaultView }}</p>
              </div>
            </div>
            <div class="result-item__body__col">
              <div class="label">{{ 'Studio:' | t }}</div>
              <div class="data">
                <p>{{ item.room | defaultView }}</p>
              </div>
            </div>
            <div class="result-item__body__col">
              <div class="label">{{ 'Instructor:' | t }}</div>
              <div class="data">
                <p>{{ item.instructor | defaultView }}</p>
              </div>
            </div>
            <div class="result-item__body__col result-item_result-item__body__col--btn">
              <span
                tabindex="0"
                class="app-btn addtocalendar"
                @click.stop="AddToCalendarToggle($event)"
                @keypress.enter="AddToCalendarToggle($event)"
              >
                <var class="atc_event">
                  <var class="atc_date_start">{{ item.time_start_calendar }}</var>
                  <var class="atc_date_end">{{ item.time_end_calendar }}</var>
                  <var class="atc_timezone">{{ item.timezone }}</var>
                  <var class="atc_title">{{ item.class_info.title }}</var>
                  <var class="atc_description">{{ item.class_info.description }}</var>
                  <var class="atc_location">{{ item.location }}</var>
                  <var class="atc_organizer"></var>
                  <var class="atc_organizer_email"></var>
                </var>
              </span>
            </div>
          </div>
        </article>
      </template>
    </div>

    <SchedulesModal
      v-if="results.length"
      :modal-data="modalData || results[0]"
      :modal-show="modalShowing"
      @close-modal="modalShowing = false"
    />
  </div>
</template>

<script>
import SchedulesModal from './SchedulesModal'
import AppPreloader from './AppPreloader'

export default {
  components: {
    SchedulesModal,
    AppPreloader
  },
  filters: {
    defaultView(item) {
      return item ? item : '-'
    }
  },
  props: {
    resultsNumber: {
      type: Number,
      default: 0
    },
    results: {
      type: Array,
      default: null
    },
    date: {
      type: String,
      default: ''
    },
    showPreloader: {
      type: Boolean,
      default: true
    },
    noResultUrl: {
      type: String,
      default: ''
    },
  },
  data() {
    return {
      modalData: null,
      modalShowing: false,
      dateStamp: null
    }
  },
  computed: {
    DateDisplay() {
      const date = new Date(this.dateStamp)
      const we = new Intl.DateTimeFormat('en', { weekday: 'long' }).format(date)
      const mo = new Intl.DateTimeFormat('en', { month: 'long' }).format(date)
      const da = new Intl.DateTimeFormat('en', { day: 'numeric' }).format(date)
      return `${we}, ${mo} ${da}`
    }
  },
  watch: {
    date() {
      this.dateStamp = Date.parse(this?.date)
    }
  },
  mounted() {
    document.addEventListener('click', this.CloseCalendarDropdown)
  },
  updated() {
    this.CalendarButtonInit()
  },
  destroyed() {
    document.removeEventListener('click', this.CloseCalendarDropdown)
  },
  methods: {
    showModal(row) {
      this.modalData = row
      this.modalShowing = true
    },
    ChangeDate(dir) {
      const date = new Date(this.dateStamp)
      if (dir > 0) date.setDate(date.getDate() + 1)
      else date.setDate(date.getDate() - 1)
      const dateFormatted = `${date.getFullYear()}-${('0' + (date.getMonth() + 1)).slice(-2)}-${(
        '0' + date.getDate()
      ).slice(-2)}`

      this.$emit('date-change', dateFormatted)
    },
    AddToCalendarToggle(event) {
      event.target.classList.toggle('open')
    },
    CalendarButtonInit() {
      window.addtocalendar.load()
      const arr = document.querySelectorAll('.atcb-link')
      for (let btn of arr) {
        btn.innerHTML = 'Add to calendar'
      }
    },
    CloseCalendarDropdown() {
      const arr = document.querySelectorAll('.addtocalendar')
      for (let btn of arr) {
        btn.classList.remove('open')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.app-results {
  margin-top: 16px;
  font-family: $font-main;

  @include media-breakpoint-up(lg) {
    margin-top: 0;
  }

  &__bar {
    display: flex;
    flex-direction: column-reverse;

    @include media-breakpoint-up(lg) {
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      background: $color-gray-dark;
      padding: 8px 20px 7px;
    }

    &__number {
      background: $color-gray-dark;
      font-family: $font-secondary;
      font-size: rem(18);
      text-transform: uppercase;
      color: $color-white;
      padding: 10px 15px 9px;
      margin-top: 18px;

      @include media-breakpoint-up(lg) {
        margin-top: 0;
        padding: 0;
      }
    }

    .multiselect {
      flex-basis: auto;

      @include media-breakpoint-up(lg) {
        flex-basis: 236px;
      }
    }
  }
}

.result-item {
  padding: 17px 15px 16px;
  color: $color-black;

  &:nth-child(2n) {
    background: $color-gray-bg;
  }

  &--no-results {
    height: 100px;
    background: $color-gray-light;
    font-weight: bold;
    font-size: rem(14);
    line-height: rem(19);
    color: $color-black;
    display: flex;
    align-items: center;

    p {
      margin: 0;
    }

    span {
      font-size: rem(34);
      color: $color-green;
      display: inline-block;
      font-weight: normal;
      margin-right: 15px;
    }

    a {
      color: $color-green;
    }
  }

  &__head {
    @include media-breakpoint-up(lg) {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    &__title {
      border: 0;
      box-shadow: none;
      color: $color-blue;
      text-transform: uppercase;
      font-family: $font-secondary;
      margin-bottom: 16px;
      font-size: rem(20);
      background: transparent;
      padding: 0;
      transition: color 0.2s ease-in-out;

      &:hover {
        color: $color-green;
        cursor: pointer;
      }
    }

    &__price {
      font-size: rem(12);
      line-height: rem(17);

      mark {
        font-family: $font-secondary;
        background: transparent;
        font-size: rem(18);
        padding: 0;
      }
    }
  }

  &__body {
    font-size: rem(14);
    display: grid;
    grid-template-columns: 1fr 1fr;

    @include media-breakpoint-up(lg) {
      grid-template: 1fr / 1fr 1fr 1fr 1fr auto;
    }

    &__col {
      margin-top: 16px;

      @include media-breakpoint-up(lg) {
        margin-top: 0;
      }

      .label {
        font-weight: bold;
        margin-bottom: 10px;
      }

      p {
        margin-bottom: 5px;
      }

      &--btn {
        grid-column: 1 / 3;
        grid-row: 4;
        margin-top: 26px;

        @include media-breakpoint-up(lg) {
          grid-column: auto;
          grid-row: auto;
          margin-top: 16px;
          justify-self: end;
        }
      }
    }
  }
}

.days-pagination {
  height: 50px;
  background: $color-gray-light;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 20px;

  &__control {
    border: 0;
    background: transparent;
    box-shadow: none;
    font-size: rem(14);
    line-height: rem(19);
    font-weight: bold;
    color: $color-white;
    cursor: pointer;
    display: flex;
    align-items: center;

    &:disabled {
      opacity: 0;
      pointer-events: none;
    }

    &.label {
      cursor: default;
      font-weight: normal;
      color: $color-black;
      text-transform: uppercase;
      font-family: $font-secondary;
      font-size: rem(20);
      line-height: rem(24px);
    }

    span {
      color: $color-green;
      font-size: rem(22);
      margin-left: 10px;
    }

    &.prev {
      span {
        margin-left: 0;
        margin-right: 10px;
      }
    }
  }
}
</style>
